import React, { useState, useEffect } from 'react'
import { ArrowLeft, Palette, Check, RotateCcw, Save } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, CardHeader, CardTitle, CardContent } from '../../components/ui'
import { Toast, ToastType } from '../../components/ui/Toast'

interface ThemeColors {
 primary: string
 secondary: string
 success: string
 warning: string
 danger: string
 info: string
 background: string
 surface: string
 text: string
}

const defaultColors: ThemeColors = {
 primary: '#3b82f6', // blue-500
 secondary: '#8b5cf6', // violet-500
 success: '#10b981', // emerald-500
 warning: '#f59e0b', // amber-500
 danger: '#ef4444', // red-500
 info: '#06b6d4', // cyan-500
 background: '#ffffff', // white
 surface: '#f9fafb', // gray-50
 text: '#111827' // gray-900
}

const presetThemes = [
 {
 name: 'Azul Padrão',
 colors: {
 primary: '#3b82f6',
 secondary: '#8b5cf6',
 success: '#10b981',
 warning: '#f59e0b',
 danger: '#ef4444',
 info: '#06b6d4',
 background: '#ffffff',
 surface: '#f9fafb',
 text: '#111827'
 }
 },
 {
 name: 'Verde Esmeralda',
 colors: {
 primary: '#10b981',
 secondary: '#059669',
 success: '#22c55e',
 warning: '#f59e0b',
 danger: '#ef4444',
 info: '#06b6d4',
 background: '#ffffff',
 surface: '#f0fdf4',
 text: '#111827'
 }
 },
 {
 name: 'Roxo Moderno',
 colors: {
 primary: '#8b5cf6',
 secondary: '#a855f7',
 success: '#10b981',
 warning: '#f59e0b',
 danger: '#ef4444',
 info: '#06b6d4',
 background: '#ffffff',
 surface: '#faf5ff',
 text: '#111827'
 }
 },
 {
 name: 'Laranja Vibrante',
 colors: {
 primary: '#f97316',
 secondary: '#ea580c',
 success: '#10b981',
 warning: '#f59e0b',
 danger: '#ef4444',
 info: '#06b6d4',
 background: '#ffffff',
 surface: '#fff7ed',
 text: '#111827'
 }
 },
 {
 name: 'Azul Oceano',
 colors: {
 primary: '#0ea5e9',
 secondary: '#0284c7',
 success: '#10b981',
 warning: '#f59e0b',
 danger: '#ef4444',
 info: '#06b6d4',
 background: '#ffffff',
 surface: '#f0f9ff',
 text: '#111827'
 }
 },
 {
 name: 'Rosa Elegante',
 colors: {
 primary: '#ec4899',
 secondary: '#db2777',
 success: '#10b981',
 warning: '#f59e0b',
 danger: '#ef4444',
 info: '#06b6d4',
 background: '#ffffff',
 surface: '#fdf2f8',
 text: '#111827'
 }
 },
 {
 name: 'Tema Escuro',
 colors: {
 primary: '#3b82f6',
 secondary: '#8b5cf6',
 success: '#10b981',
 warning: '#f59e0b',
 danger: '#ef4444',
 info: '#06b6d4',
 background: '#111827',
 surface: '#1f2937',
 text: '#f9fafb'
 }
 }
]

export const ThemeSettingsPage: React.FC = () => {
 const navigate = useNavigate()
 const [colors, setColors] = useState<ThemeColors>(defaultColors)
 const [activePreview, setActivePreview] = useState<string | null>(null)
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

 useEffect(() => {
 // Carrega as cores salvas do localStorage
 const savedColors = localStorage.getItem('themeColors')
 if (savedColors) {
 const parsed = JSON.parse(savedColors)
 setColors(parsed)
 applyColors(parsed)
 }
 }, [])

 // Converte hex para o triplet "H S% L%" que os tokens shadcn (consumidos como
 // hsl(var(--token))) esperam — gravar um triplet RGB nesses tokens, como a
 // versão anterior fazia, produzia uma cor CSS inválida e era silenciosamente
 // ignorado pelo navegador.
 const hexToHslTriple = (hex: string) => {
 const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
 if (!result) return '0 0% 0%'

 const r = parseInt(result[1], 16) / 255
 const g = parseInt(result[2], 16) / 255
 const b = parseInt(result[3], 16) / 255

 const max = Math.max(r, g, b)
 const min = Math.min(r, g, b)
 let h = 0
 let s = 0
 const l = (max + min) / 2

 if (max !== min) {
 const d = max - min
 s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
 switch (max) {
 case r: h = (g - b) / d + (g < b ? 6 : 0); break
 case g: h = (b - r) / d + 2; break
 default: h = (r - g) / d + 4; break
 }
 h /= 6
 }

 return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
 }

 const applyColors = (newColors: ThemeColors) => {
 const root = document.documentElement

 // Tokens shadcn (lidos como hsl(var(--x))) — precisam do triplet HSL.
 root.style.setProperty('--primary', hexToHslTriple(newColors.primary))
 root.style.setProperty('--secondary', hexToHslTriple(newColors.secondary))
 root.style.setProperty('--success', hexToHslTriple(newColors.success))
 root.style.setProperty('--warning', hexToHslTriple(newColors.warning))
 root.style.setProperty('--destructive', hexToHslTriple(newColors.danger))
 root.style.setProperty('--info', hexToHslTriple(newColors.info))
 root.style.setProperty('--background', hexToHslTriple(newColors.background))
 root.style.setProperty('--foreground', hexToHslTriple(newColors.text))

 // --color-surface é lido diretamente como var(--color-surface) (não
 // envolvido em hsl()/rgb()), então precisa do valor hex literal.
 root.style.setProperty('--color-surface', newColors.surface)
 }

 const handleColorChange = (key: keyof ThemeColors, value: string) => {
 const newColors = { ...colors, [key]: value }
 setColors(newColors)
 applyColors(newColors)
 }

 const handlePresetSelect = (preset: typeof presetThemes[0]) => {
 setColors(preset.colors)
 applyColors(preset.colors)
 setActivePreview(preset.name)
 }

 const handleSave = () => {
 localStorage.setItem('themeColors', JSON.stringify(colors))
 setToast({ message: 'Cores salvas com sucesso!', type: 'success' })
 }

 const handleReset = () => {
 setColors(defaultColors)
 applyColors(defaultColors)
 localStorage.removeItem('themeColors')
 setActivePreview(null)
 }

 return (
 <div className="max-w-7xl mx-auto space-y-6">
 {toast && (
 <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
 )}
 {/* Header */}
 <div className="flex items-center justify-between">
 <div className="flex items-center space-x-4">
 <Button
 type="button"
 variant="outline"
 onClick={() => navigate('/settings')}
 >
 <ArrowLeft className="h-4 w-4 mr-2" />
 Voltar
 </Button>
 <div>
 <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
 <Palette className="h-6 w-6" />
 Personalização de Cores
 </h1>
 <p className="text-muted-foreground">
 Customize as cores do sistema ao seu gosto
 </p>
 </div>
 </div>
 <div className="flex gap-2">
 <Button variant="outline" onClick={handleReset}>
 <RotateCcw className="h-4 w-4 mr-2" />
 Restaurar Padrão
 </Button>
 <Button onClick={handleSave}>
 <Save className="h-4 w-4 mr-2" />
 Salvar Alterações
 </Button>
 </div>
 </div>

 {/* Temas Pré-definidos */}
 <Card>
 <CardHeader>
 <CardTitle>Temas Pré-definidos</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
 {presetThemes.map((preset) => (
 <button
 key={preset.name}
 onClick={() => handlePresetSelect(preset)}
 className={`relative p-4 rounded-xl border-2 transition-all hover:shadow-lg ${
 activePreview === preset.name
 ? 'border-accent bg-accent/5 dark:bg-primary-900/20'
 : 'border-border hover:border-primary-300'
 }`}
 >
 <div className="flex items-center justify-between mb-3">
 <span className="text-sm font-semibold text-foreground">
 {preset.name}
 </span>
 {activePreview === preset.name && (
 <Check className="h-4 w-4 text-accent" />
 )}
 </div>
 <div className="flex gap-2">
 <div
 className="w-8 h-8 rounded-full shadow-sm"
 style={{ backgroundColor: preset.colors.primary }}
 />
 <div
 className="w-8 h-8 rounded-full shadow-sm"
 style={{ backgroundColor: preset.colors.secondary }}
 />
 <div
 className="w-8 h-8 rounded-full shadow-sm"
 style={{ backgroundColor: preset.colors.success }}
 />
 </div>
 </button>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Cores Customizadas */}
 <Card>
 <CardHeader>
 <CardTitle>Cores Customizadas</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Cor Primária
 </label>
 <div className="flex gap-3 items-center">
 <input
 type="color"
 value={colors.primary}
 onChange={(e) => handleColorChange('primary', e.target.value)}
 className="h-12 w-20 rounded-lg cursor-pointer border border-border"
 />
 <input
 type="text"
 value={colors.primary}
 onChange={(e) => handleColorChange('primary', e.target.value)}
 className="flex-1 px-3 py-2 border border-border rounded-lg dark:text-white font-mono text-sm"
 />
 </div>
 <p className="text-xs text-muted-foreground mt-1">Botões principais, links e elementos de destaque</p>
 </div>

 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Cor Secundária
 </label>
 <div className="flex gap-3 items-center">
 <input
 type="color"
 value={colors.secondary}
 onChange={(e) => handleColorChange('secondary', e.target.value)}
 className="h-12 w-20 rounded-lg cursor-pointer border border-border"
 />
 <input
 type="text"
 value={colors.secondary}
 onChange={(e) => handleColorChange('secondary', e.target.value)}
 className="flex-1 px-3 py-2 border border-border rounded-lg dark:text-white font-mono text-sm"
 />
 </div>
 <p className="text-xs text-muted-foreground mt-1">Elementos secundários e complementares</p>
 </div>

 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Cor de Sucesso
 </label>
 <div className="flex gap-3 items-center">
 <input
 type="color"
 value={colors.success}
 onChange={(e) => handleColorChange('success', e.target.value)}
 className="h-12 w-20 rounded-lg cursor-pointer border border-border"
 />
 <input
 type="text"
 value={colors.success}
 onChange={(e) => handleColorChange('success', e.target.value)}
 className="flex-1 px-3 py-2 border border-border rounded-lg dark:text-white font-mono text-sm"
 />
 </div>
 <p className="text-xs text-muted-foreground mt-1">Mensagens de sucesso e confirmações</p>
 </div>

 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Cor de Aviso
 </label>
 <div className="flex gap-3 items-center">
 <input
 type="color"
 value={colors.warning}
 onChange={(e) => handleColorChange('warning', e.target.value)}
 className="h-12 w-20 rounded-lg cursor-pointer border border-border"
 />
 <input
 type="text"
 value={colors.warning}
 onChange={(e) => handleColorChange('warning', e.target.value)}
 className="flex-1 px-3 py-2 border border-border rounded-lg dark:text-white font-mono text-sm"
 />
 </div>
 <p className="text-xs text-muted-foreground mt-1">Alertas e avisos importantes</p>
 </div>

 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Cor de Erro
 </label>
 <div className="flex gap-3 items-center">
 <input
 type="color"
 value={colors.danger}
 onChange={(e) => handleColorChange('danger', e.target.value)}
 className="h-12 w-20 rounded-lg cursor-pointer border border-border"
 />
 <input
 type="text"
 value={colors.danger}
 onChange={(e) => handleColorChange('danger', e.target.value)}
 className="flex-1 px-3 py-2 border border-border rounded-lg dark:text-white font-mono text-sm"
 />
 </div>
 <p className="text-xs text-muted-foreground mt-1">Erros e ações destrutivas</p>
 </div>

 <div>
 <label className="block text-sm font-medium text-foreground mb-2">
 Cor de Informação
 </label>
 <div className="flex gap-3 items-center">
 <input
 type="color"
 value={colors.info}
 onChange={(e) => handleColorChange('info', e.target.value)}
 className="h-12 w-20 rounded-lg cursor-pointer border border-border"
 />
 <input
 type="text"
 value={colors.info}
 onChange={(e) => handleColorChange('info', e.target.value)}
 className="flex-1 px-3 py-2 border border-border rounded-lg dark:text-white font-mono text-sm"
 />
 </div>
 <p className="text-xs text-muted-foreground mt-1">Mensagens informativas</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Preview */}
 <Card>
 <CardHeader>
 <CardTitle>Pré-visualização</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <div className="flex flex-wrap gap-3">
 <button
 className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
 style={{ backgroundColor: colors.primary }}
 >
 Botão Primário
 </button>
 <button
 className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
 style={{ backgroundColor: colors.secondary }}
 >
 Botão Secundário
 </button>
 <button
 className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
 style={{ backgroundColor: colors.success }}
 >
 Sucesso
 </button>
 <button
 className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
 style={{ backgroundColor: colors.warning }}
 >
 Aviso
 </button>
 <button
 className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
 style={{ backgroundColor: colors.danger }}
 >
 Erro
 </button>
 <button
 className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
 style={{ backgroundColor: colors.info }}
 >
 Info
 </button>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
 <div className="p-4 rounded-lg border-2" style={{ borderColor: colors.primary }}>
 <h3 className="font-bold mb-2" style={{ color: colors.primary }}>Card Primário</h3>
 <p className="text-sm text-muted-foreground">
 Este é um exemplo de card com borda primária
 </p>
 </div>
 <div className="p-4 rounded-lg" style={{ backgroundColor: colors.surface }}>
 <h3 className="font-bold mb-2" style={{ color: colors.text }}>Card com Fundo</h3>
 <p className="text-sm text-muted-foreground">
 Este card usa a cor de superfície definida
 </p>
 </div>
 <div className="p-4 rounded-lg border-2" style={{ borderColor: colors.success }}>
 <h3 className="font-bold mb-2" style={{ color: colors.success }}>Card de Sucesso</h3>
 <p className="text-sm text-muted-foreground">
 Exemplo com cor de sucesso
 </p>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 )
}

